import { useMemo } from "react";

import { NullAnswers } from "@/components/EvaluationCreateForm/EvaluationCreateForm.decl";
import {
  extractCategoryAndQuestionIndexes,
  extractQuestions,
} from "@/components/EvaluationCreateForm/EvaluationCreateForm.utility";
import { useEvaluationCreateForm } from "../useEvaluationCreateForm/useEvaluationCreateForm";

export function useEvaluationCreateFormInteractions() {
  const { form } = useEvaluationCreateForm();

  const overallScore = form.useStore((state) =>
    state.values.categories.reduce(
      (total, category) =>
        total +
        category.questions.reduce((categoryTotal, question) => {
          const possibleAnswers =
            question.questionPossibleBinaryAnswers ??
            question.questionPossibleRatingAnswers;

          if (!possibleAnswers) {
            return categoryTotal;
          }

          const value = question.binaryAnswer ?? question.ratingAnswer;

          if (
            value === "NOT_APPLICABLE" ||
            value === null ||
            value === undefined
          ) {
            return categoryTotal;
          }

          const found = possibleAnswers.find(
            (possibleAnswer) => possibleAnswer.value === value
          );

          if (!found) {
            return categoryTotal;
          }

          return categoryTotal + found.points;
        }, 0),
      0
    )
  );

  const notApplicablePointsPerCategory = form.useStore((state) =>
    state.values.categories.flatMap((category) =>
      category.questions.reduce((accu, question) => {
        const value = question.binaryAnswer ?? question.ratingAnswer;

        // If the question is not applicable we should not count it.
        if (value !== "NOT_APPLICABLE") {
          return accu;
        }

        return accu + question.questionTotalPoints;
      }, 0)
    )
  );

  const maximumPointsByCategory = form.useStore((state) =>
    state.values.categories.flatMap((category) =>
      category.questions.reduce(
        (accu, question) => accu + question.questionTotalPoints,
        0
      )
    )
  );

  const overallMaxScore = useMemo(() => {
    const withoutNotApplicableQuestionPoints = maximumPointsByCategory.map(
      (points, index) =>
        points - (notApplicablePointsPerCategory.at(index) ?? 0)
    );

    return withoutNotApplicableQuestionPoints.reduce(
      (accu, points) => accu + points,
      0
    );
  }, [maximumPointsByCategory, notApplicablePointsPerCategory]);

  // If we have nullAnswers we should not be able to submit the form.
  // [2, [0, 1, 2]] means that the category at index 2 has null questions at index 0, 1, 2.
  const nullAnswers: NullAnswers = form.useStore((state) =>
    state.values.categories.flatMap(extractCategoryAndQuestionIndexes)
  );

  const hasUnansweredQuestions = useMemo(
    () => nullAnswers.flatMap(extractQuestions).length,
    [nullAnswers]
  );

  return {
    overallScore,
    overallMaxScore,
    nullAnswers,
    hasUnansweredQuestions,
  };
}
